import api from "./api";

/**
 * Service providers — the people a customer books.
 *
 * Deliberately separate from userService: that manages portal logins, this
 * manages the people who perform services. Every provider works under a login
 * (`user_id`), which the create and edit forms both require. The API calls the
 * resource `staff`; the portal calls it "Providers" so the existing Staff
 * screen keeps its meaning.
 */

/** Form state -> the payload the API expects. */
const toPayload = (values) => {
  const payload = {
    display_name: { en: values.nameEn, ar: values.nameAr },
    job_title: { en: values.jobTitleEn ?? "", ar: values.jobTitleAr ?? "" },
    bio: { en: values.bioEn ?? "", ar: values.bioAr ?? "" },
    user_id: values.userId || null,
    employment_status: values.employmentStatus || "active",
    is_bookable_online: Boolean(values.isBookableOnline),
    sort_order: Number(values.sortOrder) || 0,
  };

  // Only sent when the form actually offered the fields — a caller without
  // staff.manage_commission never sees them, and the API ignores them anyway.
  if (values.commissionType != null) {
    payload.commission_type = values.commissionType;
    payload.commission_value =
      values.commissionValue === "" || values.commissionValue == null
        ? 0
        : Number(values.commissionValue);
  }

  // Absent means "leave what they have"; an empty array means "assign none".
  if (values.branchIds) payload.branches = values.branchIds;
  if (values.serviceIds) payload.services = values.serviceIds;

  return payload;
};

/**
 * GET /business/staff
 * `filters` accepts { search, branch, service, employmentStatus, isBookableOnline }.
 * The boolean is left out entirely when it is "" (no filter), since false is a
 * real value.
 */
export const fetchProviders = async ({ page = 1, perPage = 15, filters = {} } = {}) => {
  const params = { page, per_page: perPage };

  if (filters.search) params.search = filters.search;
  if (filters.branch) params.branch = filters.branch;
  if (filters.service) params.service = filters.service;
  if (filters.employmentStatus) params.employment_status = filters.employmentStatus;
  if (filters.isBookableOnline !== "" && filters.isBookableOnline != null) {
    params.is_bookable_online = filters.isBookableOnline;
  }

  const { data } = await api.get("/business/staff", { params });
  return { providers: data.data.staff, meta: data.meta };
};

/** GET /business/staff/{id} */
export const fetchProvider = async (id) => {
  const { data } = await api.get(`/business/staff/${id}`);
  return data.data.staff;
};

/** POST /business/staff */
export const createProvider = async (values) => {
  const { data } = await api.post("/business/staff", toPayload(values));
  return data.data.staff;
};

/** PUT /business/staff/{id} */
export const updateProvider = async (id, values) => {
  const { data } = await api.put(`/business/staff/${id}`, toPayload(values));
  return data.data.staff;
};

/** DELETE /business/staff/{id} — soft delete; eligibility survives a restore. */
export const deleteProvider = (id) => api.delete(`/business/staff/${id}`);

/** PUT /business/staff/{id}/branches — replaces the whole set. */
export const syncProviderBranches = async (id, branchIds) => {
  const { data } = await api.put(`/business/staff/${id}/branches`, { branches: branchIds });
  return data.data.staff;
};

/** PUT /business/staff/{id}/services — staff eligibility, from this side. */
export const syncProviderServices = async (id, serviceIds) => {
  const { data } = await api.put(`/business/staff/${id}/services`, { services: serviceIds });
  return data.data.staff;
};

/** POST /business/staff/{id}/photo — multipart; replaces any existing photo. */
export const uploadProviderPhoto = async (id, file) => {
  const body = new FormData();
  body.append("photo", file);

  const { data } = await api.post(`/business/staff/${id}/photo`, body, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.data.staff;
};

/** DELETE /business/staff/{id}/photo */
export const deleteProviderPhoto = (id) => api.delete(`/business/staff/${id}/photo`);

/**
 * GET /business/staff/{id}/schedule
 * Every branch they work at unless `branchId` narrows it.
 */
export const fetchProviderSchedule = async (id, branchId = null) => {
  const params = branchId ? { branch: branchId } : {};
  const { data } = await api.get(`/business/staff/${id}/schedule`, { params });
  return data.data.schedule;
};

/**
 * PUT /business/staff/{id}/schedule — replaces their week at one branch, for
 * one period. Other branches they cover are untouched, and so is the same
 * branch's other periods: saving the summer roster leaves the winter one alone.
 *
 * A period is a **type**, a **start** and a **count** — never an end date.
 * The one exception to "one period per save" is `replaces_legacy`, which also
 * clears a roster written before periods existed and which therefore has no
 * end at all.
 */
export const saveProviderSchedule = async (id, branchId, schedule, period = {}) => {
  const { data } = await api.put(`/business/staff/${id}/schedule`, {
    branch_id: branchId,
    // The period, not a date range: the server computes the end from these
    // three on the real calendar. An end date sent from here would be a value
    // the client could get wrong, and it is not read.
    period_type: period.type,
    starts_on: period.startsOn,
    periods_count: Number(period.count),
    // Only ever true when somebody is deliberately replacing a roster that
    // predates periods and has no end. Defaulted off server-side too.
    ...(period.replacesLegacy ? { replaces_legacy: true } : {}),
    schedule,
  });
  return data.data.schedule;
};

/** GET /business/staff/{id}/leave */
export const fetchProviderLeave = async (id, { page = 1, perPage = 15, filters = {} } = {}) => {
  const params = { page, per_page: perPage };

  if (filters.upcoming) params.upcoming = 1;
  if (filters.type) params.type = filters.type;

  const { data } = await api.get(`/business/staff/${id}/leave`, { params });
  return { leave: data.data.leave, meta: data.meta };
};

/** Leave form state -> payload. An all-day leave carries no times. */
const toLeavePayload = (values) => {
  const allDay = Boolean(values.isAllDay);

  const payload = {
    type: values.type,
    starts_on: values.startsOn,
    ends_on: values.endsOn,
    is_all_day: allDay,
    starts_at: allDay ? null : values.startsAt || null,
    ends_at: allDay ? null : values.endsAt || null,
  };

  // Only sent when the caller may write it; the API drops it otherwise.
  if (values.note != null) payload.note = values.note;

  return payload;
};

/** POST /business/staff/{id}/leave */
export const createProviderLeave = async (id, values) => {
  const { data } = await api.post(`/business/staff/${id}/leave`, toLeavePayload(values));
  return data.data.leave;
};

/** PUT /business/staff/{id}/leave/{leaveId} */
export const updateProviderLeave = async (id, leaveId, values) => {
  const { data } = await api.put(`/business/staff/${id}/leave/${leaveId}`, toLeavePayload(values));
  return data.data.leave;
};

/** DELETE /business/staff/{id}/leave/{leaveId} */
export const deleteProviderLeave = (id, leaveId) =>
  api.delete(`/business/staff/${id}/leave/${leaveId}`);

/** PUT /business/services/{id}/staff — eligibility from the service's side. */
export const syncServiceProviders = async (serviceId, staffIds) => {
  const { data } = await api.put(`/business/services/${serviceId}/staff`, { staff: staffIds });
  return data.data.service;
};
